import React, { useCallback, useEffect, useState } from 'react';
import { usePermission } from '../../hooks/usePermission';
import { describeApiError, type DialogContent } from '../../utils/errorPresentation';
import { listPackingAlerts, resolvePackingAlerts, type PackingAlertKind, type PackingAlertProduct } from '../../lib/packingAlertsApi';
import { notifyReviewQueueChanged } from '../../lib/catalogueSubmissionsApi';

/**
 * The "check the packing" list (D29, MC-5, USC-BUG-0029): products whose
 * packaging a Master Catalogue editor should look at, across every supplier.
 *
 * Deborah asked for a warning instead of checking every item herself. Anyone
 * with Master Catalogue view sees the list; the tick boxes and "Mark checked"
 * appear only with edit rights, and the server refuses otherwise (Q9).
 *
 * MOVED off the Master Catalogue page onto the Review page, 2026-09-23 — the
 * owner's words, "out of the modal buried in the Master Catalogue page where
 * nobody finds it." This component used to be the `Modal` itself
 * (`isOpen`/`onClose`, only fetching while open); it now renders INLINE and
 * always loads — the Review page decides when it is on screen, the same way
 * every other section of that page works. Only the SCREEN moved: the route
 * (`GET /api/packing-alerts`), the permission rule and
 * `lib/packingAlertsApi.ts` are exactly as they were.
 */

export const PACKING_ALERTS_PAGE_SIZE = 50;

const KIND_LABELS: Record<PackingAlertKind, string> = {
  pack_count_missing: 'No case packaging',
  pack_size_looks_like_one_item: 'Case size looks like one item',
  pack_unit_unrecognised: 'Pack unit not understood',
  unit_size_missing: 'No unit size',
  memory_disagrees: 'Invoice disagrees with item memory',
  catalogue_contradicts: 'Contradicts the catalogue',
  per_unit_jump: 'Unusual price jump',
  unchecked_edit: 'Packaging edit waiting for a check',
  packaging_edited: 'Packaging edited — take a second look',
  catalogue_packaging_incomplete: 'Needs packaging review',
};

const remembered = (row: PackingAlertProduct, field: 'splitUow' | 'splitPackCount' | 'splitUnitSize') => {
  const value = row.fields?.[field]?.value;
  return value === null || value === undefined || value === '' ? '—' : String(value);
};

export interface PackingAlertsPanelProps {
  /**
   * Reports the pending total up to a caller — the Review page's section
   * heading ("Packing alerts (M)") and, from there, the sidebar badge — every
   * time it changes, rather than each of them issuing their own `limit: 1`
   * read of the same list this component already fetches.
   */
  onTotalChange?: (total: number) => void;
}

export default function PackingAlertsPanel({ onTotalChange }: PackingAlertsPanelProps = {}) {
  // Master Catalogue edit (Q9): a button the server would refuse is not offered.
  const { canEdit } = usePermission('products');
  const [rows, setRows] = useState<PackingAlertProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DialogContent | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPackingAlerts({ page: p, limit: PACKING_ALERTS_PAGE_SIZE });
      setRows(res.data || []);
      const nextTotal = res.meta?.total ?? 0;
      setTotal(nextTotal);
      onTotalChange?.(nextTotal);
      setSelected(new Set());
    } catch (e: any) {
      setError(describeApiError(e, { action: 'Load packing alerts', fallback: 'Could not load the packing alerts.' }));
    } finally {
      setLoading(false);
    }
  }, [onTotalChange]);

  // Always loads — no `isOpen` gate any more. The Review page mounts this only
  // when its section is on screen, same as every other list on that page.
  useEffect(() => {
    load(page);
  }, [page, load]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function markChecked() {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await resolvePackingAlerts(ids);
      setNotice(`${res.data.resolved} product${res.data.resolved === 1 ? '' : 's'} marked checked.`);
      // The sidebar badge's count is stale the moment this resolves — same
      // event `catalogueSubmissionsApi` dispatches after a submission
      // decision, so one listener covers both halves of the Review queue.
      notifyReviewQueueChanged();
      // The last rows of a later page: step back rather than show an empty page.
      if (rows.length === ids.length && page > 1) setPage((p) => p - 1);
      else await load(page);
    } catch (e: any) {
      setError(describeApiError(e, { action: 'Mark checked', fallback: 'Could not mark those alerts checked.' }));
    } finally {
      setBusy(false);
    }
  }

  const pages = Math.max(1, Math.ceil(total / PACKING_ALERTS_PAGE_SIZE));

  return (
      <div className="flex h-full flex-col gap-3">
        <p className="text-xs text-slate-600">
          Products whose packaging should be checked in the Master Catalogue. Alerts only warn: they never stop an
          invoice being confirmed. Marking a product checked is remembered, so the same printed packaging does not
          raise it again; a different pack from the supplier does. A packaging edit waiting for a check is accepted as
          its invoice line holds it now, so to reject one, correct that invoice line first. A catalogue row marked
          “Needs packaging review” has nothing to mark checked: it leaves the list once its packaging is completed in
          the Master Catalogue.
        </p>
        {!canEdit && (
          <p className="text-xs font-medium text-slate-700">
            Only someone with Master Catalogue edit rights can mark these checked.
          </p>
        )}
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            <div className="font-semibold">{error.title}</div>
            {error.summary && <div className="mt-0.5">{error.summary}</div>}
            {error.hint && <div className="mt-1 text-rose-700">{error.hint}</div>}
          </div>
        )}
        {notice && (
          <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {notice}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200">
          <table className="data-table w-full text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-600">
              <tr>
                {canEdit && <th className="w-8 px-3 py-2" aria-label="Select" />}
                <th className="px-3 py-2">Supplier</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">What to check</th>
                <th className="px-3 py-2 whitespace-nowrap">Memory holds</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="px-3 py-6 text-center text-xs text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="px-3 py-6 text-center text-xs text-slate-500">
                    No packaging needs checking.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const name = row.displayName || row.itemKey;
                  return (
                    <tr key={row._id} className="border-t border-slate-100 align-top">
                      {canEdit && (
                        <td className="px-3 py-2">
                          {/* A catalogue row has no alert to mark checked (USC-BUG-0060). */}
                          {row.source !== 'catalogue' && (
                            <input
                              type="checkbox"
                              checked={selected.has(row._id)}
                              onChange={() => toggle(row._id)}
                              aria-label={`Select ${name}`}
                            />
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs text-slate-700">
                        {row.source === 'catalogue' ? 'Master Catalogue' : row.vendorName || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-slate-900">{name}</td>
                      <td className="px-3 py-2">
                        <ul className="space-y-1">
                          {row.packingAlerts.map((alert, i) => (
                            <li key={`${alert.kind}-${alert.invoiceId || ''}-${alert.lineIndex ?? ''}-${i}`} className="text-xs">
                              <span className="font-semibold text-amber-800">{KIND_LABELS[alert.kind] || alert.kind}</span>
                              {alert.invoiceNumber ? (
                                <span className="text-slate-500">
                                  {' '}
                                  · invoice {alert.invoiceNumber}
                                  {alert.lineIndex != null ? `, line ${alert.lineIndex + 1}` : ''}
                                </span>
                              ) : null}
                              {alert.editedByName ? <span className="text-slate-500"> · by {alert.editedByName}</span> : null}
                              <div className="text-slate-700">{alert.message}</div>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">
                        {remembered(row, 'splitUow')} · {remembered(row, 'splitPackCount')} · {remembered(row, 'splitUnitSize')}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-slate-200 px-2 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {pages} · {total} product{total === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-200 px-2 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          {canEdit && (
            <button
              type="button"
              disabled={!selected.size || busy}
              onClick={markChecked}
              className="rounded-md bg-primary-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
            >
              {busy ? 'Marking…' : `Mark checked${selected.size ? ` (${selected.size})` : ''}`}
            </button>
          )}
        </div>
      </div>
  );
}
